import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../../components/feature/Layout';
import { useSEO, generateWebPageSchema } from '../../../utils/seo';

const JapaneseSummaryPage: React.FC = () => {
  // FAQ設定
  const faqs = [
    {
      question: '国語が苦手な子は、どこから始めればいいですか？',
      answer: 'まずは「漢字」と「語彙」の基礎固めから始めましょう。読解問題でつまずく原因の多くは、文章中の言葉の意味が分からないことです。漢字と語彙を増やすことで、読解力も自然と向上します。',
    },
    {
      question: '読解問題が苦手な子への声かけは？',
      answer: '「何が書いてあった？」ではなく、「誰が出てきた？」「どんな場面だった？」と具体的に聞くことが効果的です。段落ごとに要点を確認しながら読むと、全体の流れが理解しやすくなります。',
    },
    {
      question: '古文・漢文の学習は、どう進めればいいですか？',
      answer: 'まずは「音読」から始めましょう。古文・漢文は声に出して読むことで、リズムや言葉の響きが体に染み込みます。意味が分からなくても、まずは音読を繰り返すことが理解への第一歩です。',
    },
    {
      question: '中学国語の無料教材サイトは、どれを選べばいいですか？',
      answer: '読解問題なら「みんぷり」、漢字練習なら「ちびむすドリル」がおすすめです。文法の基礎固めには「無料で使える中学学習プリント」が便利です。',
    },
    {
      question: '居場所や家庭で国語を教える際の注意点は？',
      answer: '「正解を教える」のではなく、「なぜそう思ったか」を聞くことが大切です。子どもの考えを否定せず、「そう考えたんだね」と受け止めた上で、一緒に答えを探しましょう。',
    },
  ];

  // SEO設定
  useSEO({
    title: '中学国語まとめ｜読解・文法・漢字の学習ガイド｜キョウコレ',
    description: '中学国語の学習内容を学年別・単元別に整理。読解・文法・漢字の分け方と、読解の型、おすすめの無料教材サイトをご紹介します。',
    keywords: '中学国語,国語まとめ,読解,文法,漢字,学習ガイド,無料教材',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/features/japanese-summary',
    jsonLd: generateWebPageSchema(
      '中学国語まとめ｜読解・文法・漢字の学習ガイド｜キョウコレ',
      '中学国語の学習内容を学年別・単元別に整理。読解・文法・漢字の分け方と、読解の型、おすすめの無料教材サイトをご紹介します。',
      '/features/japanese-summary'
    ),
  });

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-pink-50 to-orange-50 py-12">
        <div className="max-w-5xl mx-auto px-4">
          {/* ヘッダー */}
          <div className="text-center mb-12">
            <div className="inline-block w-20 h-20 bg-red-100 rounded-full flex items-center justify-center text-4xl mb-4">
              📖
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              中学国語の無料教材まとめ
            </h1>
            <p className="text-lg text-gray-600">
              学年別・分野別に整理した学習ガイドと、おすすめの無料教材サイトをご紹介
            </p>
          </div>

          {/* このページでわかること */}
          <div className="bg-white rounded-xl shadow-sm p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-4 border-red-500 pb-2">
              このページでわかること
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-red-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>中学国語を「読解」「文法」「漢字」の3つに分けて整理する方法</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-red-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>学年別・分野別のつまずきポイントと攻略法</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-red-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>無料で使える国語教材サイトの特徴と使い分け</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-red-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>居場所や家庭で国語を教える際のコツ</span>
              </li>
            </ul>
          </div>

          {/* 国語はこう整理すると進めやすい */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-red-500 pb-2">
              国語はこう整理すると進めやすい
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                中学国語は「読解」「文法」「漢字」の3つの柱で構成されています。この3つは独立しているようで、実は深くつながっています。例えば、読解問題を解くには語彙力（漢字・言葉の知識）が必要ですし、文法の知識があれば文章の構造が理解しやすくなります。
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                <strong>読解・文法・漢字の分け方：</strong>国語が苦手な子の多くは、「読解問題」でつまずいています。しかし、その原因は「読解力」ではなく、「語彙力不足」や「文法知識の欠如」であることが多いです。まずは漢字と文法の基礎を固めることが、読解力向上の近道です。
              </p>
              <div className="bg-red-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">学習の進め方（推奨順序）</h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">1</span>
                    <div>
                      <strong>漢字・語彙の基礎固め</strong>：読解の土台となる言葉の知識を増やす
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">2</span>
                    <div>
                      <strong>文法の理解</strong>：文の構造を理解し、正確に読み取る力を養う
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">3</span>
                    <div>
                      <strong>読解の型を習得</strong>：説明文・物語文・古文の読み方を学ぶ
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">4</span>
                    <div>
                      <strong>応用問題</strong>：高校受験レベルの読解問題に挑戦
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </section>

          {/* 学年別・分野別の整理 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-red-500 pb-2">
              学年別・分野別の整理
            </h2>

            {/* 中1国語 */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center mr-3">1</span>
                中1国語のポイント
              </h3>
              <p className="text-gray-700 mb-4">
                中1では「説明文・物語文の読解」「文法の基礎」「漢字」を学びます。小学校とは異なり、論理的に読み取る力が求められます。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">説明文の読解</h4>
                  <p className="text-sm text-gray-600">
                    段落ごとの要点を捉え、筆者の主張を理解する。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">物語文の読解</h4>
                  <p className="text-sm text-gray-600">
                    登場人物の心情を読み取り、場面の変化を理解する。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">文法（品詞・文の成分）</h4>
                  <p className="text-sm text-gray-600">
                    名詞・動詞・形容詞などの品詞を理解する。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">漢字（常用漢字）</h4>
                  <p className="text-sm text-gray-600">
                    中学で習う漢字を計画的に学習する。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">古文入門</h4>
                  <p className="text-sm text-gray-600">
                    古文の基本的な読み方と、歴史的仮名遣いを学ぶ。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* 中2国語 */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center mr-3">2</span>
                中2国語のポイント
              </h3>
              <p className="text-gray-700 mb-4">
                中2では「論説文の読解」「古文・漢文の理解」「文法の応用」など、より高度な内容を学びます。中1の基礎がしっかりしていれば、スムーズに進められます。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">論説文の読解</h4>
                  <p className="text-sm text-gray-600">
                    筆者の主張と根拠を整理し、論理展開を理解する。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">随筆の読解</h4>
                  <p className="text-sm text-gray-600">
                    筆者の体験と考えを読み取る。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">古文の読解</h4>
                  <p className="text-sm text-gray-600">
                    古文の文法と、古典作品の内容を理解する。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">漢文入門</h4>
                  <p className="text-sm text-gray-600">
                    漢文の基本的な読み方（返り点・書き下し文）を学ぶ。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">文法（用言の活用）</h4>
                  <p className="text-sm text-gray-600">
                    動詞・形容詞・形容動詞の活用を理解する。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* 中3国語 */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center mr-3">3</span>
                中3国語のポイント
              </h3>
              <p className="text-gray-700 mb-4">
                中3では「高度な読解問題」「古文・漢文の応用」「作文・小論文」など、高校受験につながる重要な内容を学びます。計画的な学習が必要です。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">高度な読解問題</h4>
                  <p className="text-sm text-gray-600">
                    複数の文章を比較したり、抽象的な内容を理解したりする。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">古文・漢文の応用</h4>
                  <p className="text-sm text-gray-600">
                    古典作品の内容を深く理解し、現代との比較を行う。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">作文・小論文</h4>
                  <p className="text-sm text-gray-600">
                    自分の意見を論理的に表現する力を養う。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">文法の総復習</h4>
                  <p className="text-sm text-gray-600">
                    中1・中2で学んだ文法を総復習し、高校受験に備える。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">語彙力の強化</h4>
                  <p className="text-sm text-gray-600">
                    慣用句・ことわざ・四字熟語などを習得する。<Link to="/materials?subject=国語" className="text-red-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* おすすめ無料教材サイト */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-red-500 pb-2">
              おすすめ無料教材サイト一覧
            </h2>
            <p className="text-gray-700 mb-6">
              中学国語の学習に役立つ無料教材サイトを厳選しました。それぞれの特徴を理解して、目的に合わせて使い分けましょう。
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://min-pri.com/japanese-pri/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-700 hover:underline cursor-pointer"
                  >
                    みんぷり 国語プリント
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>高校受験対策の読解・古文・漢文に特化
                </p>
                <p className="text-sm text-gray-700">
                  高校受験に特化したプリントが豊富で、読解問題から古文・漢文まで幅広く対応しています。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://happylilac.net/sy-kanzi-zyoyo.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-700 hover:underline cursor-pointer"
                  >
                    ちびむすドリル 中学漢字
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>中学・高校で習う漢字の練習とテスト。印刷向け
                </p>
                <p className="text-sm text-gray-700">
                  学年別・単元別に整理された漢字プリントが豊富です。基礎固めに特化しており、漢字が苦手な子でも取り組みやすい内容です。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://chugaku.manabihiroba.net/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-700 hover:underline cursor-pointer"
                  >
                    無料で使える中学学習プリント
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>漢字・文法・読解を総合的に学習できる
                </p>
                <p className="text-sm text-gray-700">
                  中1から中3まで、すべての単元のプリントが揃っています。基本問題から応用問題まで段階的に用意されています。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://desafiante.net/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-700 hover:underline cursor-pointer"
                  >
                    ぷりんと保管庫
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>国語の基礎力を固める問題集。自習向け
                </p>
                <p className="text-sm text-gray-700">
                  基礎固めに特化したプリントが豊富で、自宅学習に最適です。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <Link
                    to="/sites"
                    className="text-red-600 hover:text-red-700 hover:underline cursor-pointer"
                  >
                    その他の国語教材サイトを見る
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </h3>
                <p className="text-sm text-gray-700">
                  キョウコレでは、さらに多くの無料教材サイトを紹介しています。教科や単元で絞り込んで検索できます。
                </p>
              </div>
            </div>
          </section>

          {/* 居場所・家庭での使い方 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-red-500 pb-2">
              居場所・家庭での使い方
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                国語は「正解を教える」のではなく、「なぜそう思ったか」を聞くことが大切です。居場所や家庭で国語を教える際のコツをまとめました。
              </p>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">1. 「なぜそう思ったか」を聞く</h3>
                  <p className="text-gray-700 leading-relaxed">
                    読解問題で間違えたとき、すぐに正解を教えるのではなく、「なぜそう思ったの？」と聞いてみましょう。子ども自身が考えを言語化することで、思考のプロセスが明確になります。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">2. 「段落ごとに要点を確認する」</h3>
                  <p className="text-gray-700 leading-relaxed">
                    長文読解では、一度に全部読もうとすると混乱します。段落ごとに「ここは何が書いてあった？」と確認しながら読むと、全体の流れが理解しやすくなります。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">3. 「音読を習慣にする」</h3>
                  <p className="text-gray-700 leading-relaxed">
                    古文・漢文は特に、声に出して読むことで理解が深まります。「この文、声に出して読んでみようか」と声をかけて、一緒に音読する時間を作りましょう。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">4. 活用レシピを参考にする</h3>
                  <p className="text-gray-700 leading-relaxed">
                    キョウコレの<Link to="/recipes" className="text-red-600 hover:underline">活用レシピ</Link>では、他の居場所や家庭での実践例が共有されています。「こんな声かけが効果的だった」「この教材の使い方が良かった」といったアイデアを参考にしてみましょう。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-red-500 pb-2">
              よくある質問
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-start">
                    <span className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      Q
                    </span>
                    {faq.question}
                  </h3>
                  <div className="ml-11">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ボラタス導線 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-red-500 pb-2">
              オンライン学習支援を探すなら（ボラタス）
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                教材だけでなく、オンラインで学習をサポートしてくれるボランティアを探すこともできます。居場所や家庭での学習支援に、ぜひご活用ください。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://boratasu.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap text-center"
                >
                  ボラタスでボランティアを探す
                  <i className="ri-external-link-line ml-2"></i>
                </a>
                <a
                  href="https://zion-craft.work/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-slate-600 text-white font-medium rounded-lg hover:bg-slate-700 transition-colors duration-200 cursor-pointer whitespace-nowrap text-center"
                >
                  ボラタスについて詳しく知る
                  <i className="ri-external-link-line ml-2"></i>
                </a>
              </div>
            </div>
          </section>

          {/* まとめ */}
          <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-xl shadow-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">
              中学国語は、読解・文法・漢字を分けて整理しましょう
            </h2>
            <p className="text-lg leading-relaxed mb-6">
              このページで紹介した無料教材サイトや学習のコツを活用して、<br />
              子どもたちの「分かった！」を増やしていきましょう。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/materials?subject=国語"
                className="inline-block px-6 py-3 bg-white text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                国語の単元ガイド一覧を見る
              </Link>
              <Link
                to="/sites"
                className="inline-block px-6 py-3 bg-white text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                教材サイト一覧を見る
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default JapaneseSummaryPage;
